import { createStore } from 'zustand/vanilla'
import { readText, writeText } from '../services/storage'

// Stable anonymous device identifier for crowdsourced crowding reports. Persistence mirrors
// favorites.ts (localStorage, the app-private sandbox in a Capacitor WebView).
//
// A random UUID generated ONCE and reused. Not tied to any account or PII: it exists purely
// so the backend can rate-limit one report per device per vehicle, and so the K=3
// corroboration gate can count distinct devices rather than users.

const DEVICE_KEY = 'transit_device_id'

function loadOrCreateDeviceId(): string {
  const existing = readText(DEVICE_KEY, undefined, (value) => isUuid(value) ? value : undefined)
  if (existing !== undefined) return existing

  const id = generateUuid()
  writeText(DEVICE_KEY, id)
  return id
}

function isUuid(v: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)
}

function generateUuid(): string {
  // crypto.randomUUID is available in modern WebViews; fall back defensively.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // RFC4122 v4 fallback using crypto.getRandomValues when available.
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
}

interface DeviceState {
  /** Stable anonymous device id (UUID v4), persisted in localStorage. */
  deviceId: string
}

export const useDeviceStore = createStore<DeviceState>(() => ({
  deviceId: loadOrCreateDeviceId(),
}))
